import { z } from 'zod';
import { HttpUrl } from './common/url.js';
/**
 * Comments and OPVs share one shape, split by channel (FR-ENGA-02/03).
 *
 * `opv` is not a comment flavour in the UI — it is counted separately
 * (EngagementCounts.opvs vs .comments), listed separately (AC-ENGA-03.3) and
 * notified separately (AC-ENGA-03.2). One collection, one schema, hard
 * channel separation everywhere it is observable.
 */
export const CommentChannel = z.enum(['comment', 'opv']);
/** Rich comment kinds from the design: text, voice note, sticker/GIF. */
export const CommentKind = z.enum(['text', 'voice', 'sticker']);
export const CommentMedia = z.object({
    url: z.string().url(),
    /** Voice notes render a waveform with duration (AC-ENGA-02.2). */
    durationMs: z.number().int().positive().optional(),
});
/**
 * The same media as a member SENDS it, with the scheme allowlist applied.
 *
 * Separate from `CommentMedia` for the same reason `AttachmentInput` is
 * separate from `Attachment`: this shape is also `CommentView.media`, and
 * tightening it there would reject voice notes and stickers already stored
 * with a non-conforming url. A sticker url is handed to an image loader and a
 * voice note url to an audio player, neither of which should ever be asked to
 * resolve `javascript:` or `file:`.
 */
export const CommentMediaInput = CommentMedia.extend({ url: HttpUrl });
const base = {
    /** Reply target. One visible nesting level — a reply to a reply is refused (AC-ENGA-02.5). */
    parentId: z.string().optional(),
};
export const CreateCommentInput = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('text'), body: z.string().min(1).max(1_000), ...base }),
    z.object({ kind: z.literal('voice'), media: CommentMediaInput, ...base }),
    z.object({ kind: z.literal('sticker'), media: CommentMediaInput, ...base }),
]);
export const CommentView = z.object({
    id: z.string(),
    contentId: z.string(),
    channel: CommentChannel,
    authorId: z.string(),
    parentId: z.string().nullable(),
    kind: CommentKind,
    body: z.string().optional(),
    media: CommentMedia.optional(),
    likes: z.number().int().nonnegative(),
    replyCount: z.number().int().nonnegative(),
    createdAt: z.coerce.date(),
});
export const CommentLikeState = z.object({
    commentId: z.string(),
    liked: z.boolean(),
    likes: z.number().int().nonnegative(),
});
//# sourceMappingURL=comments.js.map